import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ThemeContext } from '../../../../context/theme-context';
import am5locales_tr_TR from "@amcharts/amcharts5/locales/tr_TR";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import NumberFormatter from '../../../../formatters/NumberFormatter';
import DateFormatter from '../../../../formatters/DateFormatter';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const CalculatorLineChart = (props) => {
    const numberFormatter = new NumberFormatter();
    const dateFormatter = new DateFormatter();
    const [chartData, setChartData] = useState([]);
    const [theme, setTheme] = useState('dark');
    const currentTheme = useContext(ThemeContext).theme;
    const [symbolCode, setSymbolCode] = useState('');
    const [symbolName, setSymbolName] = useState('');
    
    // Get the language code from redux store
    const { i18n } = useTranslation();
    const profileBasicDetail = useSelector(state => state.profile.basicDetail);
    const languageCode = profileBasicDetail.languagecode || i18n.language || 'tr-TR';

    useEffect(() => {
        if (props.data.length > 0) {
            setChartData(props.data);
            if (props.selectedSecurity && props.selectedSecurity.code) {
                setSymbolCode(props.selectedSecurity.code);
            }
            if (props.selectedSecurity && props.selectedSecurity.name) {
                setSymbolName(props.selectedSecurity.name);
            }
        }
        setTheme(currentTheme);
    }, [props, currentTheme]);

    useLayoutEffect(() => {
        let root = am5.Root.new(props.divId);
        root.setThemes([
            am5themes_Animated.new(root),
        ]);
        root.numberFormatter.set("numberFormat", "#,###.");
        
        // Dynamically set locale based on language code
        if (languageCode === 'en-US') {
            root.locale = am5locales_en_US;
            console.log("Using English locale for chart");
        } else {
            // Default to Turkish
            root.locale = am5locales_tr_TR;
            console.log("Using Turkish locale for chart");
        }

        var chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: true,
                panY: true,
                wheelX: "panX",
                wheelY: "zoomX",
                pinchZoomX: true,
                paddingLeft: 0,
                paddingRight: 0
            })
        );

        let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
        cursor.lineY.set("visible", false);

        // Create X-Axis
        let axisRendererX = am5xy.AxisRendererX.new(root, {
            fill: theme === 'dark' ? am5.color('#626262') : am5.color('#231f20'),
        });

        axisRendererX.labels.template.setAll({
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231f20'),
            rotation: 0, // Initial rotation
            centerY: am5.p50, // Align the labels with the center
            centerX: am5.p50, // Center alignment
        });

        let xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                maxDeviation: 1,
                groupData: false,
                baseInterval: { timeUnit: "day", count: 1 }, // Use day as time unit
                gridIntervals: [
                    { timeUnit: "day", count: 2 },
                    { timeUnit: "week", count: 2 },
                    { timeUnit: "month", count: 2 },
                    { timeUnit: "year", count: 1 }
                ],
                renderer: axisRendererX,
                minGridDistance: 60, // Start with a reasonable minGridDistance
            })
        );

        xAxis.set("minorDateFormats", {
            "day": "dd MMM",
            "month": "MM/yyyy"
        });

        xAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "hide", // Hide labels that don't fit
            maxWidth: 80, // Maximum width for the labels
            textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
        });

        xAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 0,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
        });

        // Automatically adjust label rotation based on data length
        if (chartData.length > 50) {
            xAxis.get("renderer").labels.template.setAll({
                rotation: -45, // Rotate labels if there are many data points
                centerY: am5.p50,
                centerX: am5.p100,
            });
        }

        // Create Y-Axis
        var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {
                opposite: false,
                minGridDistance: 30,
            })
        }));

        yAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 1,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
        });

        yAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "wrap",
            maxWidth: 250,
            textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
        });

        // Define the tooltip
        let tooltipLineSeries = am5.Tooltip.new(root, {
            keepTargetHover: true,
            getFillFromSprite: true,
            getStrokeFromSprite: true,
            autoTextColor: true,
            getLabelFillFromSprite: true,
            fill: am5.color("#333"),
        });

        tooltipLineSeries.get("background").setAll({
            fill: am5.color("#333"),
            fillOpacity: 0,
            strokeOpacity: 0,
            getStrokeFromSprite: true,
        });

        tooltipLineSeries.label.set("fill", am5.color("#231f20")); // White text in tooltip

        // Create Line Series for `securityValue`
        var securityValueSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'Security Value',
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "securityValue",
                valueXField: "date",
                stroke: am5.color('#42A5F5'), // Adjust color as needed
                connect: true,
                tooltip: tooltipLineSeries,
                tooltipHTML: createTooltipHTML(),
            })
        );

        securityValueSeries.strokes.template.setAll({
            strokeWidth: 2,
        });

        // Create Line Series for `benchmarkValue`
        var benchmarkValueSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'Benchmark Value',
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "benchmarkValue",
                valueXField: "date",
                stroke: am5.color('#FF5733'), // Adjust color as needed
                connect: true,
                tooltip: tooltipLineSeries,
                tooltipHTML: createTooltipHTML(),
            })
        );

        benchmarkValueSeries.strokes.template.setAll({
            strokeWidth: 2,
        });

        // Add the data to the series
        if (chartData && chartData.length > 0) {
            securityValueSeries.data.setAll(chartData);
            benchmarkValueSeries.data.setAll(chartData);
        }

        securityValueSeries.appear(1000);
        benchmarkValueSeries.appear(1000);
        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [chartData, theme, languageCode]); // Added languageCode as dependency

    const createTooltipHTML = () => {
        // Get translated strings based on current language
        const dateLabel = languageCode === 'en-US' ? 'Date' : 'Tarih';
        const priceLabel = languageCode === 'en-US' ? 'Price' : 'Fiyat';
        const benchmarkLabel = languageCode === 'en-US' ? 'Benchmark' : 'Kıyaslama';

        // Temaya göre renkleri ayarlayın
        const priceColor = theme === 'dark' ? '#2FBC99' : '#2FBC99';
        const benchmarkColor = theme === 'dark' ? '#FF5733' : '#FF5733';
        const backgroundColor = theme === 'dark' ? '#121212' : '#FFFFFF';
        const textColor = theme === 'dark' ? '#fff' : '#231f20';
        const textGray = theme === 'dark' ? '#FFFFFF' : '#757575';

        return `
            <div style="padding: 10px; background-color: ${backgroundColor}; color: ${textColor}; border-radius: 5px; min-width: 180px; border:.5px solid #b5b5b5">
                <div style="display:block; text-align:left;">
                    <strong><span style="color: ${priceColor}">${props.selectedSecurity}</span></strong>
                </div>
    
                <div style="display:block; margin-top: 5px;">
                    <label style="text-align:left; padding-left:0px;">
                        <span style="color:${priceColor}"><strong>${dateLabel}:</strong></span> 
                        <span style="color:${textGray}"> {date.formatDate('dd/MM/yyyy')}</span>
                    </label>
                </div>
    
                <div style="margin-top: 5px; display:block; margin:0;">
                    <div>
                        <span style="font-size:16px; font-family:Roboto-Medium;">${priceLabel}: </span>
                        <span style="text-align:right; float:right; margin:0; padding-left:10px; font-size:16px; font-family:Roboto-Medium;">{securityValue} ₺</span>
                    </div>
                    <div>
                        <span style="font-size:16px; font-family:Roboto-Medium; color:${benchmarkColor};">${benchmarkLabel}: </span>
                        <span style="text-align:right; float:right; margin:0; padding-left:10px; font-size:16px; font-family:Roboto-Medium;">{benchmarkValue} ₺</span>
                    </div>
                </div>
            </div>`;
    }

    return (
        <div className='index-analysis-line-chart'>
            <div id={props.divId} style={{ width: "100%", height: "400px" }}></div>
        </div>
    )
}

export default CalculatorLineChart;
